/**
 * Doserne Academy — reusable layout shell (i18n + adaptive menu side + onboarding).
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Menu, X, Home, Calendar, FolderKanban, Camera, FileText, ClipboardList,
  ShieldAlert, GraduationCap, User, LogOut, Shield, Boxes, ChevronRight,
  ShoppingBag, MessageCircle, RefreshCw, Megaphone,
  ChevronLeft, LayoutGrid, Globe, Check, Bell, BellOff, Loader2, Users as UsersIcon, FileSignature, Mail, Coins, Clock,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { useLang, LANGS } from "./i18n";
import DoserneOnboarding from "./DoserneOnboarding";
import { useDosernePush } from "./useDosernePush";
import { toast } from "sonner";
import ChatFab from "./ChatFab";
import CalculatorFab from "./CalculatorFab";
import CalendarReminderPopup from "./CalendarReminderPopup";
import IncomingCallModal from "./IncomingCallModal";
import InstallPrompt from "./InstallPrompt";
import DoserneInstallBanner from "./DoserneInstallBanner";
import { TrainingLockedScreen } from "./DoserneTrainingGate";
import { useDoserneManifest, useDeferredInstallPrompt } from "./useDoserneInstall";
import { useDoserneAppBadge } from "./useDoserneAppBadge";
import { useSwUpdate } from "./useSwUpdate";
import { formatAppVersionWithDate } from "../../appVersion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU_META = {
  home:      { icon: Home,          to: "/doserne",              tkey: "nav.home" },
  plan:      { icon: Calendar,      to: "/doserne/plan",         tkey: "nav.daily_plan" },
  projects:  { icon: FolderKanban,  to: "/doserne/prosjekter",   tkey: "nav.projects" },
  customers: { icon: UsersIcon,     to: "/doserne/kunder",       tkey: "nav.customers",  adminOnly: true },
  contracts: { icon: FileSignature, to: "/doserne/admin/kontrakter", tkey: "nav.contracts", adminOnly: true },
  email:     { icon: Mail,          to: "/doserne/email",        tkey: "nav.email",     adminOnly: true },
  progress:  { icon: Camera,        to: "/doserne/progresjon",   tkey: "nav.progress" },
  colleagues:{ icon: UsersIcon,     to: "/doserne/kolleger",     tkey: "nav.colleagues" },
  timeliste: { icon: Clock,         to: "/doserne/timeliste",    tkey: "nav.timeliste" },
  timesheet: { icon: Coins,         to: "/doserne/pontaj",       tkey: "nav.timesheet", adminOnly: true },
  kurs:      { icon: GraduationCap, to: "/doserne/kurs",         tkey: "nav.kurs" },
  klaer:     { icon: ShoppingBag,   to: "/doserne/klaer",        tkey: "nav.klaer" },
  chat:      { icon: MessageCircle, to: "/doserne/chat",         tkey: "nav.chat" },
  reports:   { icon: Megaphone,     to: "/doserne/sesizari",     tkey: "nav.reports" },
  underlag:  { icon: FileText,      to: "/underlag",             tkey: "nav.underlag" },
  kontroll:  { icon: ClipboardList, to: "/apps/kontroll",        tkey: "nav.kontroll" },
  sja:       { icon: ShieldAlert,   to: "/doserne/sja",          tkey: "nav.sja" },
  capacity:  { icon: LayoutGrid,    to: "/admin/project-overview", tkey: "nav.capacity" },
  lager:     { icon: Boxes,         to: "/doserne/lager",        tkey: "nav.lager" },
  profile:   { icon: User,          to: "/doserne/profil",       tkey: "nav.profile" },
};

function DoserneLogo({ compact = false, t }) {
  return (
    <div className="inline-flex items-center gap-2">
      <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center shadow-sm">
        <Shield className="w-5 h-5 text-cyan-400" strokeWidth={2.5} />
      </div>
      {!compact && (
        <div className="flex flex-col leading-tight">
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Doserne</span>
          <span className="text-lg font-black text-slate-900 -mt-0.5">Academy</span>
        </div>
      )}
    </div>
  );
}

function PushToggle({ authHeader }) {
  const { supported, permission, subscribed, busy, enable, disable } = useDosernePush(authHeader);
  const { t } = useLang();
  const [testing, setTesting] = React.useState(false);

  if (!supported) return null;
  const permBlocked = permission === "denied";

  const onClick = async () => {
    try {
      if (subscribed) {
        await disable();
        toast.success(t("push.toast.disabled"));
      } else {
        await enable();
        // In the same user gesture, also ask for mic + camera so the
        // first ever incoming call can be answered immediately without
        // a separate permission prompt getting in the way. The browser
        // remembers the grant for future call sessions.
        try {
          const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
          s.getTracks().forEach((tr) => tr.stop());
          toast.success(t("push.toast.enabled") + " · mic + kamera OK");
        } catch {
          // Notifications succeeded even if the user declined the
          // A/V prompt — that's fine, we'll ask again on their first
          // outgoing call.
          toast.success(t("push.toast.enabled"));
        }
      }
    } catch (e) {
      toast.error(e?.message || t("push.toast.failed"));
    }
  };

  const onTest = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(
        `${API}/doserne/push/test`,
        {},
        authHeader(),
      );
      const okCount = (data.results || []).filter((r) => r.ok).length;
      if (okCount === 0) {
        toast.error(`Push feilet på alle ${data.total} enheter — sjekk /var/log`);
      } else {
        toast.success(`Test-varsel sendt til ${okCount} av ${data.total} enheter — sjekk telefonen`);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // 404 = no subscription in DB for this user. Silently rebuild
      // the subscription (permission is already granted) and retry.
      if (status === 404) {
        try {
          toast.info("Aktiverer varsler på nytt…");
          await enable();
          const { data } = await axios.post(`${API}/doserne/push/test`, {}, authHeader());
          const okCount = (data.results || []).filter((r) => r.ok).length;
          if (okCount === 0) toast.error(`Push feilet på ${data.total} enheter etter reaktivering`);
          else toast.success(`Test-varsel sendt til ${okCount} av ${data.total} enheter`);
        } catch (er2) {
          toast.error(er2?.message || "Kunne ikke reaktivere varsler");
        }
      } else {
        toast.error(detail || e?.message || "Kunne ikke sende test-varsel");
      }
    }
    setTesting(false);
  };

  return (
    <div className="px-5 py-3 border-t border-slate-100 space-y-2">
      <button
        type="button"
        disabled={busy || permBlocked}
        onClick={onClick}
        className={`w-full flex items-center gap-2 justify-center py-2 rounded-lg text-xs font-bold uppercase tracking-widest border transition ${
          subscribed
            ? "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100"
            : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        data-testid="push-toggle"
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : subscribed ? (
          <Bell className="w-3.5 h-3.5" />
        ) : (
          <BellOff className="w-3.5 h-3.5" />
        )}
        {permBlocked ? t("push.blocked") : subscribed ? t("push.on") : t("push.enable")}
      </button>
      {subscribed && (
        <button
          type="button"
          disabled={testing}
          onClick={onTest}
          className="w-full flex items-center gap-2 justify-center py-1.5 rounded-lg text-[11px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-slate-200 transition disabled:opacity-50"
          data-testid="push-test"
        >
          {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
          Send test-varsel
        </button>
      )}
      {permBlocked && (
        <div className="text-[10px] text-slate-400 text-center mt-1">
          {t("push.blocked_help")}
        </div>
      )}
    </div>
  );
}

function LangSwitch({ lang, setLang, t }) {
  return (
    <div className="px-5 py-4 border-t border-slate-100">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2 inline-flex items-center gap-1">
        <Globe className="w-3 h-3" /> {t("profile.language.title")}
      </div>
      <div className="flex gap-1">
        {LANGS.map((L) => {
          const active = lang === L.code;
          return (
            <button
              key={L.code}
              onClick={() => setLang(L.code)}
              type="button"
              className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg border transition text-xs font-semibold ${active ? "border-cyan-500 bg-cyan-50 text-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
              data-testid={`lang-${L.code}`}
            >
              <span>{L.flag}</span> {L.code.toUpperCase()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// FloatingMenuButton — draggable menu shortcut (mobile only). Users can drag
// it anywhere on-screen to avoid the chat FAB, phone-controls, or an on-screen
// keyboard. Position is persisted per-device in localStorage. A short tap
// still opens the drawer — we only treat a pointer as a drag after ~5 px of
// movement, so accidental taps don't reposition it.
// -----------------------------------------------------------------------------
const FLOATING_MENU_KEY = "DOSERNE_FLOATING_MENU_POS_V1";
const BTN_SIZE = 56; // matches w-14 h-14
const EDGE_PAD = 8;

function clampToViewport({ x, y }) {
  const w = window.innerWidth || 0;
  const h = window.innerHeight || 0;
  return {
    x: Math.max(EDGE_PAD, Math.min(x, w - BTN_SIZE - EDGE_PAD)),
    y: Math.max(EDGE_PAD, Math.min(y, h - BTN_SIZE - EDGE_PAD)),
  };
}

function FloatingMenuButton({ onOpen, defaultSide }) {
  const [pos, setPos] = useState(null); // {x,y} in viewport px, or null (use default anchor)
  const [dragging, setDragging] = useState(false);
  const btnRef = useRef(null);
  const stateRef = useRef({ startX: 0, startY: 0, origX: 0, origY: 0, moved: false });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(FLOATING_MENU_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p?.x === "number" && typeof p?.y === "number") {
          setPos(clampToViewport(p));
        }
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!pos) return undefined;
    const onResize = () => setPos((cur) => (cur ? clampToViewport(cur) : cur));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos]);

  const onPointerDown = (e) => {
    if (!btnRef.current) return;
    if (e.button && e.button !== 0) return;
    btnRef.current.setPointerCapture?.(e.pointerId);
    const rect = btnRef.current.getBoundingClientRect();
    stateRef.current = {
      startX: e.clientX, startY: e.clientY,
      origX: rect.left, origY: rect.top, moved: false,
    };
  };
  const onPointerMove = (e) => {
    const s = stateRef.current;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.moved) {
      if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      s.moved = true;
      setDragging(true);
    }
    setPos(clampToViewport({ x: s.origX + dx, y: s.origY + dy }));
  };
  const onPointerUp = (e) => {
    const s = stateRef.current;
    btnRef.current?.releasePointerCapture?.(e.pointerId);
    if (s.moved) {
      const rect = btnRef.current?.getBoundingClientRect();
      if (rect) {
        try {
          localStorage.setItem(FLOATING_MENU_KEY, JSON.stringify({ x: rect.left, y: rect.top }));
        } catch { /* quota */ }
      }
      setDragging(false);
      setTimeout(() => { s.moved = false; }, 50);
    } else {
      onOpen();
    }
  };

  const style = pos
    ? { color: "#fff", left: `${pos.x}px`, top: `${pos.y}px`, right: "auto", bottom: "auto", touchAction: "none" }
    : { color: "#fff", bottom: "1rem", [defaultSide === "left" ? "left" : "right"]: "1rem", touchAction: "none" };

  return (
    <button
      ref={btnRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      className={`sm:hidden fixed z-40 w-14 h-14 rounded-full bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700 shadow-lg shadow-cyan-500/40 flex items-center justify-center border-4 border-white select-none ${dragging ? "cursor-grabbing scale-110" : "cursor-grab transition-transform"}`}
      style={style}
      aria-label="Open menu"
      data-testid="floating-menu-btn"
    >
      <Menu className="w-6 h-6" strokeWidth={2.5} />
    </button>
  );
}


function InstallAppRow({ t, install }) {
  // Uses the new InstallPrompt component under the hood — same
  // beforeinstallprompt detection, but with a richer iOS "Add to
  // Home Screen" visual guide and a follow-up modal that requests
  // Notifications + microphone + camera in one go once the user
  // finishes installing. Zero extra props needed.
  const { installed } = install;
  if (installed) return null;
  return (
    <div className="px-5 py-4 border-t border-slate-100">
      <div className="w-full flex justify-center">
        <InstallPrompt />
      </div>
    </div>
  );
}



function Drawer({ open, onClose, side, menuItems, isAdmin, user, onLogout, t, lang, setLang, authHeader, install, badges }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { onClose(); }, [location.pathname]);
  useEffect(() => {
    if (open) {
      const orig = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = orig; };
    }
  }, [open]);

  const isLeft = side === "left";
  const closedTransform = isLeft ? "-translate-x-full" : "translate-x-full";
  const posClass = isLeft ? "left-0" : "right-0";

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        data-testid="drawer-backdrop"
      />
      <aside
        className={`fixed top-0 ${posClass} z-50 h-full w-[85vw] max-w-[320px] bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${open ? "translate-x-0" : closedTransform}`}
        data-testid="doserne-drawer"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="min-w-0">
            <DoserneLogo t={t} />
            <div
              className="mt-1 text-[10px] font-black tracking-wide text-slate-400 uppercase select-none"
              title="App version · last deploy"
              data-testid="drawer-app-version"
            >
              {formatAppVersionWithDate(lang || "no")}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" data-testid="drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-slate-900 flex items-center justify-center text-sm font-black" style={{ color: "#fff" }}>
              {(user?.first_name || user?.full_name || "?")[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-slate-900 truncate">{user?.full_name || user?.first_name || user?.email}</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">{isAdmin ? t("profile.role.admin") : t("profile.role.member")}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {/* First-time onboarding: install the PWA + enable push. Auto-hides
              once both goals are met or the user taps "Senere". */}
          {authHeader && <DoserneInstallBanner authHeader={authHeader} />}
          {menuItems.map((item) => {
            const meta = MENU_META[item.id];
            if (!meta) return null;
            if (meta.adminOnly && !isAdmin) return null;
            const Icon = meta.icon;
            const active = location.pathname === meta.to;
            const label = t(meta.tkey);
            const badgeCount = (badges || {})[item.id] || 0;
            const glow = badgeCount > 0 && !active;
            const cls = `flex items-center gap-3 px-5 py-3 text-sm font-medium transition ${
              active
                ? "bg-cyan-50 text-cyan-700 border-r-4 border-cyan-500"
                : meta.soon
                  ? "text-slate-400 cursor-not-allowed"
                  : glow
                    ? "text-slate-900 bg-rose-50 doserne-menu-glow"
                    : "text-slate-700 hover:bg-slate-50 active:bg-slate-100"
            }`;
            const badgeEl = badgeCount > 0 ? (
              item.id === "kurs" ? (
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-[11px] font-black shadow-sm doserne-badge-pulse"
                  style={{ color: "#fff" }}
                  data-testid={`menu-badge-${item.id}`}
                  title={`${badgeCount}`}
                >
                  {badgeCount > 99 ? "99+" : badgeCount}
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-rose-500 text-[9px] font-black uppercase tracking-wider shadow-sm doserne-badge-pulse"
                  style={{ color: "#fff" }}
                  data-testid={`menu-badge-${item.id}`}
                >
                  {t("nav.new_badge")}{badgeCount > 1 ? ` ${badgeCount}` : ""}
                </span>
              )
            ) : null;
            if (meta.soon) {
              return (
                <div key={item.id} className={cls} data-testid={`menu-${item.id}`}>
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.8} />
                  <span className="flex-1">{label}</span>
                  <span className="text-[9px] uppercase font-bold text-slate-400 border border-slate-200 rounded-full px-2 py-0.5">{t("nav.coming_soon")}</span>
                </div>
              );
            }
            if (meta.external) {
              return (
                <a key={item.id} href={meta.to} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`menu-${item.id}`}>
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.8} />
                  <span className="flex-1">{label}</span>
                  {badgeEl}
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </a>
              );
            }
            return (
              <Link key={item.id} to={meta.to} className={cls} data-testid={`menu-${item.id}`}>
                <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.8} />
                <span className="flex-1">{label}</span>
                {badgeEl}
                {active && <ChevronRight className="w-4 h-4 text-cyan-500" />}
              </Link>
            );
          })}
          {isAdmin && (
            <Link
              to="/doserne/admin/kurs"
              className="flex items-center gap-3 px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 border-t border-slate-100 mt-2"
              data-testid="menu-admin-kurs"
            >
              <GraduationCap className="w-5 h-5" strokeWidth={1.8} />
              <span className="flex-1">Kursinnstillinger</span>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/doserne/consum-jarvis"
              className="flex items-center gap-3 px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 border-t border-slate-100 mt-2"
              data-testid="menu-jarvis-costs"
            >
              <Coins className="w-5 h-5" strokeWidth={1.8} />
              <span className="flex-1">Consum Jarvis</span>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/admin/permissions"
              className="flex items-center gap-3 px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 border-t border-slate-100 mt-2"
              data-testid="menu-admin"
            >
              <LayoutGrid className="w-5 h-5" strokeWidth={1.8} />
              <span className="flex-1">{t("nav.admin")}</span>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </Link>
          )}
        </nav>

        <PushToggle authHeader={authHeader} />
        <LangSwitch lang={lang} setLang={setLang} t={t} />
        <InstallAppRow t={t} install={install} />

        <div className="px-5 py-4 border-t border-slate-200">
          <button
            onClick={() => { onLogout(); navigate("/doserne"); }}
            className="w-full flex items-center gap-2 justify-center py-2.5 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 active:bg-slate-100"
            data-testid="drawer-logout"
          >
            <LogOut className="w-4 h-4" /> {t("auth.log_out")}
          </button>
          <div className="text-[10px] text-slate-400 text-center mt-3">© {new Date().getFullYear()} {t("app.copyright")}</div>
        </div>
      </aside>
    </>
  );
}

export default function DoserneShell({ children, title, subtitle, showWelcome = false, wide = false }) {
  const { user, logout, authHeader, refresh } = useDoserneAuth();
  const { lang, setLang, menuSide, t, ready: langReady } = useLang();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuItems, setMenuItems] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Per-tab unread counts keyed by menu id. Anything > 0 makes the
  // corresponding menu row pulse with a red "NOU" pill.
  const [badges, setBadges] = useState({});
  const [gate, setGate] = useState(null); // training gate status
  const isAdmin = !!user?.is_admin;
  const shellNavigate = useNavigate();
  // Course-completion gate: until a non-admin finishes ALL required courses they
  // may only use the courses area + their own profile. `gate.blocked` (from the
  // backend) already excludes admins. Hide every other nav entry so they can't
  // reach public pages (underlag / sja / kontroll) and see projects.
  const coursesLocked = !!gate?.blocked;
  const visibleMenuItems = coursesLocked
    ? [{ id: "kurs" }, { id: "profile" }]
    : (menuItems || []);

  // Swap the document manifest to the Doserne-scoped one for the whole
  // /doserne surface. This means the browser's own "Add to Home Screen"
  // (or Safari share-menu on iOS) always installs the app scoped to
  // /doserne — not just when the onboarding banner is visible.
  useDoserneManifest();
  useDoserneAppBadge();
  const { updateAvailable } = useSwUpdate();
  const { canInstall, installed, install, isIOS } = useDeferredInstallPrompt();

  const loadMenu = useCallback(async () => {
    try {
      const [{ data }, permsRes] = await Promise.all([
        axios.get(`${API}/doserne/menu-prefs`, authHeader()),
        axios.get(`${API}/me/permissions`, authHeader()).catch(() => ({ data: {} })),
      ]);
      const items = (data.items || []).filter((i) => i.visible !== false);
      // Auto-inject `reports` for legacy users whose stored menu prefs
      // predate this feature. Inserted just before `profile` so it sits
      // at the bottom of the "communication" cluster.
      if (!items.some((it) => it.id === "reports")) {
        const profileIdx = items.findIndex((it) => it.id === "profile");
        const item = { id: "reports" };
        if (profileIdx >= 0) items.splice(profileIdx, 0, item);
        else items.push(item);
      }
      const perms = permsRes.data || {};
      const canCapacity =
        !!perms.is_builtin_admin ||
        ["view", "edit"].includes(perms?.permissions?.admin_capacity);
      if (canCapacity && !items.some((it) => it.id === "capacity")) {
        const profileIdx = items.findIndex((it) => it.id === "profile");
        const capItem = { id: "capacity" };
        if (profileIdx >= 0) items.splice(profileIdx, 0, capItem);
        else items.push(capItem);
      }
      // Admin-only "Contracts" tab (render filter hides it for non-admins).
      if (!items.some((it) => it.id === "contracts")) {
        const profileIdx = items.findIndex((it) => it.id === "profile");
        const cItem = { id: "contracts" };
        if (profileIdx >= 0) items.splice(profileIdx, 0, cItem);
        else items.push(cItem);
      }
      // Admin-only "Email" (Outlook-style) tab.
      if (!items.some((it) => it.id === "email")) {
        const profileIdx = items.findIndex((it) => it.id === "profile");
        const eItem = { id: "email" };
        if (profileIdx >= 0) items.splice(profileIdx, 0, eItem);
        else items.push(eItem);
      }
      // Personal timesheet (Pontaj) — visible to every member.
      if (!items.some((it) => it.id === "timeliste")) {
        const profileIdx = items.findIndex((it) => it.id === "profile");
        const tlItem = { id: "timeliste" };
        if (profileIdx >= 0) items.splice(profileIdx, 0, tlItem);
        else items.push(tlItem);
      }
      // Lager (warehouse shop) — visible to every member.
      if (!items.some((it) => it.id === "lager")) {
        const profileIdx = items.findIndex((it) => it.id === "profile");
        const lgItem = { id: "lager" };
        if (profileIdx >= 0) items.splice(profileIdx, 0, lgItem);
        else items.push(lgItem);
      }
      // Admin-only "Control Contabil" tab.
      if (!items.some((it) => it.id === "timesheet")) {
        const profileIdx = items.findIndex((it) => it.id === "profile");
        const tItem = { id: "timesheet" };
        if (profileIdx >= 0) items.splice(profileIdx, 0, tItem);
        else items.push(tItem);
      }
      setMenuItems(items);
    } catch {
      setMenuItems([
        { id: "home" }, { id: "plan" }, { id: "projects" }, { id: "progress" }, { id: "colleagues" },
        { id: "chat" }, { id: "reports" }, { id: "kurs" }, { id: "klaer" }, { id: "lager" }, { id: "underlag" }, { id: "kontroll" }, { id: "sja" }, { id: "contracts" }, { id: "email" }, { id: "timeliste" }, { id: "timesheet" }, { id: "profile" },
      ]);
    }
  }, [authHeader]);

  const checkOnboarding = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/prefs`, authHeader());
      if (!data.onboarding_completed) setShowOnboarding(true);
    } catch { /* silent */ }
  }, [authHeader]);

  useEffect(() => { if (user) { loadMenu(); checkOnboarding(); } }, [user, loadMenu, checkOnboarding]);

  // Poll unread counts per section every 45s + on tab refocus. Any
  // section with `unread > 0` makes its menu row glow + shows a red
  // "NOU" pill until the user taps into it.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchBadges = async () => {
      try {
        const reports = await axios
          .get(`${API}/doserne/reports/unread/count`, authHeader())
          .then((r) => r.data?.unread || 0)
          .catch(() => 0);
        const kurs = await axios
          .get(`${API}/doserne/kurs/mandatory-remaining`, authHeader())
          .then((r) => r.data?.count || 0)
          .catch(() => 0);
        if (cancelled) return;
        setBadges((prev) => ({ ...prev, reports, kurs }));
        axios
          .get(`${API}/doserne/kurs/gate-status`, authHeader())
          .then((r) => { if (!cancelled) setGate(r.data); })
          .catch(() => {});
      } catch { /* silent */ }
    };
    fetchBadges();
    const iv = setInterval(fetchBadges, 45000);
    const onVis = () => { if (!document.hidden) fetchBadges(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [user, authHeader]);

  // Clear the local badge + tell backend when user actually opens the
  // Reports page, so the pulse stops on the same tap.
  useEffect(() => {
    if (location.pathname === "/doserne/sesizari" && (badges.reports || 0) > 0) {
      setBadges((prev) => ({ ...prev, reports: 0 }));
      axios.post(`${API}/doserne/reports/unread/mark-read`, {}, authHeader()).catch(() => {});
    }
  }, [location.pathname, badges.reports, authHeader]);

  if (!user || !langReady) return children;

  const first = user?.first_name || (user?.full_name || "").split(" ")[0] || t("greet.hi");
  const isRight = menuSide === "right";

  return (
    <div className="light-theme notranslate min-h-screen bg-[#fafaf7]" translate="no" style={{ color: "#0f172a" }}>
      <header
        className="bg-white border-b border-slate-200 sticky top-0 z-30"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className={`max-w-6xl mx-auto px-4 h-16 min-h-[64px] flex items-center gap-3 ${isRight ? "flex-row-reverse" : ""}`}>
          <button
            onClick={() => setDrawerOpen(true)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700 text-white shadow-sm ${isRight ? "-mr-2" : "-ml-2"}`}
            aria-label="Open menu"
            data-testid="burger-btn"
          >
            <Menu className="w-5 h-5" strokeWidth={2.5} />
            <span className="text-xs font-black uppercase tracking-widest">Meny</span>
          </button>
          <Link to="/doserne" className="flex-1"><DoserneLogo t={t} /></Link>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 active:bg-slate-300 flex items-center justify-center text-slate-600 transition"
            title={t("common.refresh") || "Oppdater"}
            aria-label={t("common.refresh") || "Oppdater"}
            data-testid="header-refresh"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={2.25} />
          </button>
          <Link to="/doserne/profil" className="w-9 h-9 rounded-full bg-slate-900 flex items-center justify-center text-xs font-black" style={{ color: "#fff" }} data-testid="header-avatar">
            {(first || "?")[0].toUpperCase()}
          </Link>
        </div>
      </header>

      {/* Silent auto-update indicator. `useSwUpdate` auto-triggers
          SKIP_WAITING + reload — this tiny banner just tells the user
          why the app is briefly reloading, so they don't panic. Auto-
          disappears with the reload (~1 s later). */}
      {updateAvailable && (
        <div
          className="sticky top-16 z-20 bg-gradient-to-r from-cyan-500 to-emerald-500 shadow-md"
          data-testid="sw-update-banner"
        >
          <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-2" style={{ color: "#fff" }}>
            <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={2.5} />
            <div className="text-xs font-black uppercase tracking-wider">Oppdaterer til ny versjon…</div>
          </div>
        </div>
      )}

      {/* Floating menu button removed by user request — the sticky
          top-left "MENY" burger in the header is now the sole drawer
          entry-point on every Doserne page. */}

      {(showWelcome || title) && (
        <div className={`${wide ? "max-w-full px-6" : "max-w-6xl mx-auto px-4"} pt-6 pb-2`}>
          {showWelcome && (
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">
              {t("greet.hi")}, {first} 👋
            </div>
          )}
          {title && <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">{title}</h1>}
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
      )}

      <main className={`${wide ? "max-w-full px-2 sm:px-4" : "max-w-6xl mx-auto px-4"} py-6`} data-testid="doserne-shell-content">
        {(() => {
          const path = typeof window !== "undefined" ? window.location.pathname : "";
          const onKurs = path.startsWith("/doserne/kurs");
          const onProfile = path.startsWith("/doserne/profil");
          // Course-completion gate (internal rules): a non-admin who hasn't
          // finished ALL required courses may only use the courses area + their
          // own profile. `gate.blocked` already excludes admins.
          if (gate?.blocked && !onKurs && !onProfile) {
            return (
              <TrainingLockedScreen
                remaining={gate.remaining}
                onStart={() => shellNavigate("/doserne/kurs")}
                onLogout={logout}
                lang={lang}
              />
            );
          }
          return <>{children}</>;
        })()}
      </main>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        side={menuSide}
        menuItems={visibleMenuItems}
        isAdmin={isAdmin}
        user={user}
        onLogout={logout}
        t={t}
        lang={lang}
        setLang={setLang}
        authHeader={authHeader}
        install={{ canInstall, installed, install, isIOS }}
        badges={badges}
      />

      {showOnboarding && (
        <DoserneOnboarding
          user={user}
          authHeader={authHeader}
          onDone={() => setShowOnboarding(false)}
        />
      )}

      {/* Messenger-style floating chat (drawer + windows). Hides itself
          when the user is on the full chat page. */}
      <ChatFab />

      {/* Windows-style floating calculator, accessible from every page. */}
      <CalculatorFab />

      {/* Full-page central popup for pending calendar reminders. */}
      <CalendarReminderPopup authHeader={authHeader} />

      {/* Full-screen ringing modal for incoming voice/video calls. */}
      <IncomingCallModal authHeader={authHeader} user={user} />

    </div>
  );
}
